import { useState } from "react";
import type { VehicleCode } from "@/interface/standard/st_vehicle_code";
import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import CountBox from "@/components/ui/CountBox";
import Table from "@/components/ui/Table";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import { TableColumn } from "@/components/ui/Table";
import { useVehicleCode, useDeleteVehicleCode } from "@/hooks/standard/st_vehicle_code/useVehicleCode";
import { useEffect, useMemo } from "react";
import AddModal from "@/components/modal/standard/vehicleCode/Add";
import UpdateModal from "@/components/modal/standard/vehicleCode/Update";
import { toast } from "sonner";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import SelectBox from "@/components/ui/SelectBox";

export default function VehicleCodePage() {
  const [searchValue, setSearchValue] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState("Y"); // 기본값: 재직
  const [actualEmploymentStatus, setActualEmploymentStatus] = useState("Y");
  const [exactMatch, setExactMatch] = useState(false); // 정확히 일치 체크박스
  const [actualExactMatch, setActualExactMatch] = useState(false); // 실제 검색에 사용할 정확히 일치
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [allData, setAllData] = useState<VehicleCode[]>([]);
  const [totalElements, setTotalElements] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tableVer, setTableVer] = useState(0);
  const [selectedVehicleCode, setSelectedVehicleCode] = useState<VehicleCode | undefined>(undefined);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const deleteVehicleCode = useDeleteVehicleCode();
  const {
    data: vehicleCodeData,
    isLoading,
    error,
  } = useVehicleCode({
    page: currentPage,
    size: 20,
    number1: actualSearchValue,
    sort: "stVehicleCode,asc",
    employmentStatus: actualEmploymentStatus || undefined,
    exactMatch: actualExactMatch,
  });

  const columns: TableColumn[] = [
    { key: "stVehicleNumber1", header: "실차량번호" },
    { key: "stVehicleNumber2", header: "가상차량번호" },
    { key: "stVehicleEmploymentStatus", header: "사용여부" },
    { key: "gb1Name", header: "위탁구분" },
    { key: "gb2Name", header: "차량구분" },
    // { key: "stVehicleShape", header: "차량형태" },
    // { key: "stVehicleLicenseKind", header: "허가류" },
    // { key: "stVehicleLicenseItem", header: "허가항목" },
    // { key: "stVehicleLicenseCapa", header: "허가용량" },
    // { key: "stVehicleInstitution", header: "차고지" },
    // { key: "stVehicleMaxCapa", header: "최대용량" },
    // { key: "stVehicleLocation", header: "차고지 주소" },
    { key: "driverName", header: "기사명" },
    { key: "mobileTel", header: "전화번호" },
    { key: "companyName", header: "업체명" },
    { key: "stVehicleRemark", header: "비고", bodyAlign: "left" },
  ];

  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
  }, [actualSearchValue, actualEmploymentStatus, actualExactMatch]);

  useEffect(() => {
    if (vehicleCodeData && vehicleCodeData.content && vehicleCodeData.content.length > 0) {
      if (currentPage === 0) {
        setAllData(vehicleCodeData.content);
        setTotalElements(vehicleCodeData.totalElements || 0);
      } else {
        setAllData(prev => [...prev, ...vehicleCodeData.content]);
      }
    } else if (vehicleCodeData && (!vehicleCodeData.content || vehicleCodeData.content.length === 0)) {
      setAllData([]);
      setTotalElements(0);
    }
  }, [vehicleCodeData, currentPage]);

  const hasMore = useMemo(() => {
    if (!vehicleCodeData) return false;
    return !vehicleCodeData.last && allData.length < totalElements;
  }, [vehicleCodeData?.last, allData.length, totalElements]);

  useEffect(() => {
    if (vehicleCodeData) {
      setLoadingMore(false);
    }
  }, [vehicleCodeData]);

  useEffect(() => {
    if (selectedRows.length === 1) {
      const selectedData = allData[selectedRows[0]];
      setSelectedVehicleCode(selectedData);
    } else {
      setSelectedVehicleCode(undefined);
    }
  }, [selectedRows, allData]);

  const infiniteScrollPage = useInfiniteScrollPage({
    data: allData,
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore: hasMore,
    onDataLoad: async () => {
      if (hasMore) {
        setCurrentPage(prev => prev + 1);
        setTotalElements(prev => prev + 20);
      }
    },
  });

  const handleReset = () => {
    setSearchValue("");
    setActualSearchValue("");
    setEmploymentStatus("Y");
    setActualEmploymentStatus("Y");
    setExactMatch(false);
    setActualExactMatch(false);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
    setAllData([]);
    setTableVer(v => v + 1);
  };

  const handleSearch = () => {
    setActualSearchValue(searchValue);
    setActualEmploymentStatus(employmentStatus);
    setActualExactMatch(exactMatch);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
    setAllData([]);
    setTableVer(v => v + 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleUpdate = (): void => {
    setAllData([]);
    setTotalElements(0);
    setCurrentPage(0);
    setSelectedRows([]);
    setSelectedVehicleCode(undefined);
    setTableVer(v => v + 1);
  };

  const handleUpdateClick = () => {
    if (selectedRows.length === 0) {
      toast.warning("수정할 차량을 선택해주세요.");
      return;
    }
    if (selectedRows.length > 1) {
      toast.warning("하나의 차량만 선택해주세요.");
      return;
    }
    setIsUpdateModalOpen(true);
  };

  const handleDeleteClick = async () => {
    if (selectedRows.length === 0) {
      toast.warning("삭제할 차량을 선택해주세요.");
      return;
    }
    if (!window.confirm(`선택한 ${selectedRows.length}개의 차량을 삭제하시겠습니까?`)) {
      return;
    }
    try {
      for (const index of selectedRows) {
        const vehicle = allData[index];
        if (vehicle?.stVehicleCode) {
          await deleteVehicleCode.mutateAsync(vehicle.stVehicleCode);
        }
      }
      handleUpdate();
    } catch (error) {
      console.error("삭제 중 오류:", error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex gap-3 items-end">
          <SelectBox
            label="재직여부"
            value={employmentStatus}
            onChange={value => {
              setEmploymentStatus(value);
              setActualEmploymentStatus(value);
              setCurrentPage(0);
              setTotalElements(0);
              setLoadingMore(false);
              setAllData([]);
              setTableVer(v => v + 1);
            }}
            className="w-30"
            options={[
              { value: "", label: "전체" },
              { value: "Y", label: "재직" },
              { value: "N", label: "퇴직" },
            ]}
          />
          <InputBox
            label="실차량번호"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-50"
            placeholder="실차량번호 검색"
          />
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={totalElements} />
          <div className="flex gap-2">
            <PermissionButton
              permission="canCreate"
              variant="etc"
              onClick={() => {
                setSelectedRows([]);
                setSelectedVehicleCode(undefined);
                setIsAddModalOpen(true);
              }}
            >
              추가
            </PermissionButton>
            <PermissionButton permission="canUpdate" variant="etc" onClick={handleUpdateClick}>
              수정
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc" onClick={handleDeleteClick}>
              삭제
            </PermissionButton>
            <PrintButton data={allData} columns={columns} title="차량코드" />
            <ExcelButton data={allData} columns={columns} fileName="차량코드" />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`vehicle-code-table-v${tableVer}`}
            {...infiniteScrollPage.tableProps}
            className="h-full"
            columns={columns}
            data={allData}
            showCheckbox={true}
            showRowNumber={true}
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            loadingMore={loadingMore}
            maxHeight="100%"
          />
        </div>
        {isLoading && allData.length > 0 && <div className="text-center py-2 text-gray-500">새 페이지 로딩 중...</div>}
        {allData.length === 0 && (
          <div className="text-center py-2 text-gray-500">
            데이터가 없습니다. (isLoading: {isLoading.toString()}, allData.length: {allData.length})
          </div>
        )}
      </main>
      <AddModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleUpdate} />
      <UpdateModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedVehicleCode(undefined);
          setSelectedRows([]);
        }}
        onUpdate={handleUpdate}
        vehicleData={selectedVehicleCode}
      />
    </div>
  );
}
